import React, { useMemo, useState } from "react";
import { useSelf } from "../../../common/hooks/useSelf";
import { DataRequestDetail } from "../../../common/models";
import { DataSubjectRequestEventEmbeddedDto } from "../../../common/service/server/dto/DataSubjectRequestEventDto";
import { UserDetailsDto } from "../../../common/service/server/dto/UserDto";
import { Iso8601Date } from "../../../common/service/server/Types";
import { Fmt } from "../../formatters";
import { DATE_FORMAT_MEDIUM } from "../../formatters/date";
import { Callout, CalloutVariant } from "../Callout";
import { UserActionRow } from "./UserActionRow";
import { Accordion, AccordionDetails, AccordionSummary, Link, Tooltip } from "@mui/material";
import { RouteHelpers } from "../../root/routes";
import { ExpandMore } from "@mui/icons-material";

/**
 * What type of user is viewing this event list? Determines e.g., which event types to show.
 */
export enum EventListAudience {
  POLARIS_USERS,
  CONSUMERS,
}

type Props = { request: DataRequestDetail; reverse?: boolean; audience?: EventListAudience };

// Only summarize/combine the following event types:
const SUMMARIZE_EVENT_TYPES = new Set(["VENDOR_CONTACTED"]);
const createSummaryEvent = (
  group: DataSubjectRequestEventEmbeddedDto[],
): DataSubjectRequestEventEmbeddedDto => {
  const firstEvent = group[0];

  // Depending on the event type, summarize the title, message, etc. fields in different ways.

  let title = "";
  switch (firstEvent.eventType) {
    case "VENDOR_CONTACTED":
      title = `${group.length} vendors contacted`;
      break;
    default:
      title = `${firstEvent.eventType} (${group.length})`;
  }

  let message = "";
  let summarizedToLine = "";
  let representativeMessage = "";
  switch (firstEvent.eventType) {
    case "VENDOR_CONTACTED":
      // For vendor-contacted, use a HTML list
      summarizedToLine = `<ul class="user-action-row__message__recipient_list">${group
        .map((e) => {
          const lines = e.message.split("\n");
          const toLine = lines[0].startsWith("To:") ? lines[0] : "";
          return toLine ? `<li>${toLine.slice(4).trim()}</li>` : "";
        })
        .filter(Boolean)
        .join("")}</ul>`;

      representativeMessage = firstEvent.message.split("\n").slice(1).join("\n");
      message = `To: ${summarizedToLine}${
        representativeMessage ? `\n${representativeMessage}` : ""
      }`;
      break;
    default:
      // Any other types, the default will be to concatenate their messages (but ideally, create a shorter summary).
      message = group
        .map((e) => e.message)
        .filter(Boolean)
        .join("\n");
      break;
  }

  let description = "";
  switch (firstEvent.eventType) {
    case "VENDOR_CONTACTED":
      description = ""; // No 2nd line description for vendor contacted.
      break;
    default:
      description = firstEvent.eventTypeDescription;
      break;
  }

  // Since all the events that opt-in to being consolidated like this are the same type, use the first event's values as
  // the remaining fields (e.g., actor, timestamp, etc.). A user can expand-all to see the specific details if desired.
  return {
    ...firstEvent,
    eventType: "FRONTEND_EVENT_SUMMARY",
    message: message,
    eventTypeTitle: title,
    eventTypeDescription: description,
  };
};

export const EventList = ({
  request,
  reverse = false,
  audience = EventListAudience.POLARIS_USERS,
}: Props) => {
  const [self] = useSelf();
  const [expandAll, setExpandAll] = useState<boolean>(false);

  // Simple check to determine if closed callout should be shown
  const closedDate = request.closedAt ? new Date(request.closedAt).valueOf() : undefined;
  const showClosedMsg = Boolean(closedDate && Date.now() - closedDate <= 5000);

  const orderedEvents = useMemo(
    () => (reverse ? [...request.events].reverse() : request.events),
    [reverse, request],
  );

  // We may have many events in a row for some event types (like Vendor Contacted). Consolidate those to a summary event
  // unless the user is in "expand all" mode, in which case show the individual events.
  const consolidatedEvents = useMemo(() => {
    if (expandAll) {
      return orderedEvents;
    }

    const consolidated: DataSubjectRequestEventEmbeddedDto[] = [];
    let currentGroup: DataSubjectRequestEventEmbeddedDto[] = [];

    for (const event of orderedEvents) {
      if (
        currentGroup.length === 0 ||
        (currentGroup[0].eventType === event.eventType &&
          SUMMARIZE_EVENT_TYPES.has(event.eventType))
      ) {
        currentGroup.push(event); // New group or type matches current group, add event to the candidate group.
      } else {
        // Not a match, summarize candidate group (or push it directly if only 1 event); start a new candidate group.
        if (currentGroup.length > 1) {
          consolidated.push(createSummaryEvent(currentGroup));
        } else {
          consolidated.push(currentGroup[0]);
        }
        currentGroup = [event];
      }
    }

    // After looping all events we'll have the current working group in the candidate list. Consolidate into a summary
    // event if there's more than 1, or directly append the single event to the final list.
    if (currentGroup.length > 1) {
      consolidated.push(createSummaryEvent(currentGroup));
    } else if (currentGroup.length === 1) {
      consolidated.push(currentGroup[0]);
    }

    return consolidated;
  }, [orderedEvents, expandAll]);

  return (
    <>
      {showClosedMsg && (
        <Callout variant={CalloutVariant.Green} className={"mb-std"}>
          <p>
            The {request?.batch ? "requests have" : "request has"} been closed and moved to the
            “Closed” tab.
          </p>
        </Callout>
      )}

      {request?.batch && (
        <Callout variant={CalloutVariant.LightGrayBordered} className={"mb-std"}>
          <p>
            This request ({request?.id}) was part of a batch. The events shown reflect this specific
            request.
          </p>
          <Accordion className="mt-md">
            <AccordionSummary expandIcon={<ExpandMore />}>
              Jump to other requests in this batch
            </AccordionSummary>
            <AccordionDetails>
              <ul>
                {request?.batch?.requestPublicIds?.map((rqId) => (
                  <li key={rqId}>
                    {rqId == request?.id ? (
                      <>{rqId} (this request)</>
                    ) : (
                      <Link href={RouteHelpers.requests.detail(rqId)}>{rqId}</Link>
                    )}
                  </li>
                ))}
              </ul>
            </AccordionDetails>
          </Accordion>
        </Callout>
      )}

      <div className={"user-action-rows"}>
        {consolidatedEvents
          .filter((e: DataSubjectRequestEventEmbeddedDto) =>
            audience === EventListAudience.POLARIS_USERS
              ? e.isUserFacingEvent
              : e.isConsumerFacingEvent,
          )
          .map((e: DataSubjectRequestEventEmbeddedDto) => (
            <EventRow
              key={e.eventId}
              event={e}
              self={self}
              request={request}
              onClickExpandAll={() => {
                setExpandAll(true);
              }}
            />
          ))}
      </div>
    </>
  );
};

const fmtDateForRow = (date?: Iso8601Date, relative = false): string => {
  return relative ? Fmt.Date.fmtDateFromNow(date) : Fmt.Date.fmtDate(date, DATE_FORMAT_MEDIUM);
};

const fmtDateAndTimeUTC = (date?: Iso8601Date): string => {
  if (!date) {
    return "";
  }

  return new Date(date).toISOString().split("T").join(" ").slice(0, 19) + " UTC";
};

const fmtDateAndTimeLocal = (date?: Iso8601Date): string => {
  if (!date) {
    return "";
  }

  return new Date(date).toLocaleString(undefined, { timeZoneName: "short" }); // `undefined` for locale will use user's local time.
};

const showDeclaration = ({
  event,
  request,
}: {
  event: DataSubjectRequestEventEmbeddedDto;
  request: DataRequestDetail;
}) =>
  event.eventType === "CREATED" &&
  request.dataRequestType.requestTypeSlug === "CCPA_RIGHT_TO_KNOW" &&
  request.selfDeclarationProvided;

const EventRow = ({
  event,
  self,
  request,
  onClickExpandAll,
}: {
  event: DataSubjectRequestEventEmbeddedDto;
  self: UserDetailsDto;
  request: DataRequestDetail;
  onClickExpandAll?: () => void;
}) => {
  let message = event.message;

  if (event.eventType === "CREATED") {
    message = undefined;
  }

  return (
    <UserActionRow user={event.actor} type={event.eventType}>
      <div className="user-action-row__header">
        <div className="user-action-row__type">{event.eventTypeTitle}</div>
        <div className={"user-action-row__identity"}>
          {event.isRequesterAction
            ? Fmt.DataSubject.fmtFullName(request)
            : Fmt.User.fmtFullName(event.actor, self)}
        </div>
        <div className={"user-action-row__date"}>{fmtDateForRow(event.eventDate)}</div>
        <Tooltip title={fmtDateAndTimeUTC(event.eventDate)}>
          <div className={"user-action-row__date_utc"}>{fmtDateAndTimeLocal(event.eventDate)}</div>
        </Tooltip>
      </div>
      {!["REQUESTOR_CONTACTED", "VENDOR_CONTACTED"].includes(event.eventType) && (
        <div className={"user-action-row__desc"}>{event.eventTypeDescription}</div>
      )}
      {showDeclaration({ event, request }) && (
        <>
          <div className={"user-action-row__declaration_signature"}>
            Declaration electronically signed at{" "}
            <strong>{Fmt.Date.fmtDate(event.createdAt, Fmt.Date.DATE_TIME_FORMAT_YMD)}</strong>
          </div>
        </>
      )}
      {message && (
        <div
          className="user-action-row__message"
          dangerouslySetInnerHTML={{
            __html: message,
          }}
        />
      )}
      {event.eventType === "FRONTEND_EVENT_SUMMARY" && onClickExpandAll && (
        <div>
          <small>
            <em>
              This is a summarized event.{" "}
              <a
                href="#"
                onClick={(e) => {
                  onClickExpandAll();
                  e.preventDefault();
                }}
              >
                View all events
              </a>
              .
            </em>
          </small>
        </div>
      )}
    </UserActionRow>
  );
};
